/**
 * Exclusion patterns for structurally ambiguous experimental routes, kept in
 * their own module so they never reach the CloudFront Functions bundle.
 *
 * ## Why this is a separate file
 *
 * `PUBLIC_DOC_CATEGORY_SLUGS_PATTERN` is a ~375-byte alternation of every
 * public-doc category slug. rolldown cannot tree-shake object properties, so
 * while this regex lived as a field inside the `experimentalRoutes` literal,
 * it was retained in the edge bundle — which reaches that literal through
 * `edgeExperimentsByPathPattern` — even though the edge function never
 * evaluates it. That is >10% of the 3,500-byte budget in `packages/infra/src/web/edge.ts`
 * spent on a value the edge cannot use, and the budget is enforced at deploy
 * time with a `FunctionSizeLimitExceeded` failure observed at 3,945 bytes.
 *
 * The edge only ever walks entries that HAVE experiments (see
 * `edgeExperimentsByPathPattern`), and no excluded route currently runs one —
 * so dropping exclusions from its view is a no-op today. That coincidence is
 * load-bearing, so `test/unit/analytics/experimental-routes.test.ts` asserts
 * it: adding an experiment to an excluded route fails there rather than
 * silently bucketing category landing pages.
 *
 * Mirrors the `edge-client-flags.ts` / `public-doc-category-slugs.ts`
 * edge-isolation pattern: when the edge must not see something, the reliable
 * mechanism is a module that physically does not contain it.
 */
import { PUBLIC_DOC_CATEGORY_SLUGS_PATTERN } from '@listening/api-contract/public-doc-category-slugs';
import { experimentalRoutes } from './experimental-routes';

/**
 * `/doc/$docId` URL pattern matches BOTH doc-detail pages and category
 * landing pages — only the `CATEGORY_GROUPS` taxonomy can tell them apart.
 * The route file makes this call at runtime via `getPublicDocCategoryFromSlug`
 * (`apps/start/src/routes/_marketing/doc/$docId.tsx:80`); we mirror it here
 * with a regex over the same source-of-truth slug list.
 *
 * `PUBLIC_DOC_CATEGORY_SLUGS` is pre-computed in `@listening/api-contract`
 * with an inline edge-safe slugifier — the parity test in
 * `test/unit/analytics/public-doc-category-slugs.test.ts` pins it to
 * `slugifyTitle` so the two never drift.
 */
const PUBLIC_DOC_CATEGORY_EXCLUDE_PATTERN = new RegExp(
  `^/doc/(?:${PUBLIC_DOC_CATEGORY_SLUGS_PATTERN})/?$`,
  'i',
);

/**
 * Route id → second-pass filter. A path matching the route's `pattern` AND its
 * exclusion is treated as outside the experiment: the SSR resolver still
 * produces a non-enrolled `EdgeFlags` map for it (so `applyEdgeFlags`'s
 * cleanup paths run — super-property unregister, stale override removal), but
 * no variant is assigned and no cache fragmentation occurs.
 */
export const routeExcludePatterns: Partial<
  Record<keyof typeof experimentalRoutes, RegExp>
> = {
  '/_marketing/doc/$docId': PUBLIC_DOC_CATEGORY_EXCLUDE_PATTERN,
};

/**
 * True if the pathname matches an experimental route's main `pattern` but also
 * matches its exclusion (e.g. `/doc/<category-slug>` matches the `/doc/$docId`
 * route but is a category landing page, not a doc-detail page).
 *
 * Callers should still pass the route's `experiments` to the resolver so a
 * non-enrolled `EdgeFlags` map is produced — the map's presence (vs
 * `undefined`) is what lets `applyEdgeFlags` clean up stale super-properties
 * and overrides from prior sessions where the user WAS enrolled. Don't
 * shortcut to returning `undefined`/empty for excluded paths.
 */
export function isExperimentalPathExcluded(pathname: string): boolean {
  for (const [routeId, entry] of Object.entries(experimentalRoutes)) {
    if (entry.cacheMode !== 'cdn-shared') continue;
    if (!entry.pattern.test(pathname)) continue;
    const exclude =
      routeExcludePatterns[routeId as keyof typeof experimentalRoutes];
    return exclude !== undefined && exclude.test(pathname);
  }
  return false;
}
